import { Controller } from "@hotwired/stimulus";
import "./component.scss";

export default class extends Controller {
  static targets = ["dialog", "trigger"];
  static values = { topLevelPaths: Array };

  connect() {
    this.mobileMediaQuery = window.matchMedia("(width <= 50em)");
    this.boundSyncVisibility = this.syncVisibility.bind(this);
    this.boundPrepareForCache = this.prepareForCache.bind(this);
    this.boundHandleLinkClick = this.handleLinkClick.bind(this);
    this.boundHandleKeyboardInteraction = this.handleKeyboardInteraction.bind(this);

    this.mobileMediaQuery.addEventListener("change", this.boundSyncVisibility);
    window.addEventListener("popstate", this.boundSyncVisibility);
    document.addEventListener("turbo:load", this.boundSyncVisibility);
    document.addEventListener("turbo:frame-load", this.boundSyncVisibility);
    document.addEventListener("turbo:before-cache", this.boundPrepareForCache);
    document.addEventListener("turbo:before-visit", this.boundPrepareForCache);
    document.addEventListener("keydown", this.boundHandleKeyboardInteraction, true);
    this.dialogTarget.addEventListener("click", this.boundHandleLinkClick, true);

    this.syncVisibility();
  }

  disconnect() {
    this.mobileMediaQuery.removeEventListener("change", this.boundSyncVisibility);
    window.removeEventListener("popstate", this.boundSyncVisibility);
    document.removeEventListener("turbo:load", this.boundSyncVisibility);
    document.removeEventListener("turbo:frame-load", this.boundSyncVisibility);
    document.removeEventListener("turbo:before-cache", this.boundPrepareForCache);
    document.removeEventListener("turbo:before-visit", this.boundPrepareForCache);
    document.removeEventListener("keydown", this.boundHandleKeyboardInteraction, true);
    this.dialogTarget.removeEventListener("click", this.boundHandleLinkClick, true);
    this.finishClose({ restoreFocus: false });
    this.clearVisibleState();
  }

  open(event) {
    if (!this.mobileMediaQuery.matches || !this.isTopLevelPath()) return;
    if (this.dialogTarget.open) return;

    this.syncPointerInteraction(event);
    this.dialogTarget.showModal();
    this.triggerTarget.setAttribute("aria-expanded", "true");
    document.body.classList.add("no-scroll");
    document.documentElement.classList.add("no-scroll-gutter");
  }

  close(event) {
    this.syncPointerInteraction(event);
    this.animateClose({ restoreFocus: true });
  }

  handleCancel(event) {
    event.preventDefault();
    this.element.removeAttribute("data-pointer-interaction");
    this.animateClose({ restoreFocus: true });
  }

  handleDialogClick(event) {
    if (event.target === this.dialogTarget) {
      this.element.setAttribute("data-pointer-interaction", "");
      this.animateClose({ restoreFocus: true });
    }
  }

  handleLinkClick(event) {
    const link = event.target.closest("a");
    if (!link) return;

    if (link.dataset.action?.includes("dialog#show")) {
      this.finishClose({ restoreFocus: false });
      return;
    }

    requestAnimationFrame(() => this.finishClose({ restoreFocus: false }));
  }

  prepareForCache() {
    this.finishClose({ restoreFocus: false });
  }

  syncVisibility() {
    const visible = this.mobileMediaQuery.matches && this.isTopLevelPath();
    this.triggerTarget.hidden = !visible;
    document.body.classList.toggle("mobile-location-drawer-trigger-visible", visible);

    if (!visible) this.finishClose({ restoreFocus: false });
  }

  isTopLevelPath() {
    const currentPath = this.normalizePath(window.location.pathname);

    return this.topLevelPathsValue.some((path) => this.normalizePath(path) === currentPath);
  }

  normalizePath(path) {
    return path === "/" ? path : path.replace(/\/+$/, "");
  }

  syncPointerInteraction(event) {
    this.element.toggleAttribute("data-pointer-interaction", event?.detail > 0);
  }

  handleKeyboardInteraction() {
    this.element.removeAttribute("data-pointer-interaction");
  }

  animateClose({ restoreFocus }) {
    if (!this.dialogTarget.open || this.dialogTarget.dataset.closing !== undefined) return;

    this.dialogTarget.dataset.closing = "";

    setTimeout(() => {
      delete this.dialogTarget.dataset.closing;
      this.finishClose({ restoreFocus });
    }, 250);
  }

  finishClose({ restoreFocus }) {
    const wasOpen = this.dialogTarget.open;

    delete this.dialogTarget.dataset.closing;
    if (wasOpen) this.dialogTarget.close();
    this.triggerTarget.setAttribute("aria-expanded", "false");
    document.body.classList.remove("no-scroll");
    document.documentElement.classList.remove("no-scroll-gutter");

    if (restoreFocus && wasOpen && !this.triggerTarget.hidden && this.triggerTarget.isConnected) {
      requestAnimationFrame(() => this.triggerTarget.focus());
    }
  }

  clearVisibleState() {
    document.body.classList.remove("mobile-location-drawer-trigger-visible");
  }
}
